import { toast } from '@/clientWeb/components/ui/toast-utils'
import { vclog } from '../hooks/vc-log'

/**
 * Play remote audio, and cope with a browser that refuses to.
 *
 * Autoplay policy blocks `play()` on a media element until the document has
 * been interacted with. In a meeting that is usually satisfied — you clicked to
 * join — but not always: a deep link that joins on its own, a reload while the
 * tab is in the background, Safari's stricter rules, an element that mounts for
 * a peer who arrives later.
 *
 * When it does happen the result is silence, and until now the rejection went
 * into an empty catch for every ordinary peer. The one place it WAS reported
 * was the gateway path, whose comment says a swallowed rejection "is
 * indistinguishable from 'no audio arrived'". That is true of every peer; only
 * the instrumentation was scoped to whoever was being debugged at the time.
 *
 * So: remember the elements that were refused, say so once, and retry them all
 * on the next thing the user does. A click anywhere is enough — the gesture
 * does not have to be aimed at the audio.
 */

/** Elements the browser refused to start. Retried on the next gesture. */
const blocked = new Set<HTMLMediaElement>()
/** Gesture listeners are attached only while something is actually waiting. */
let listening = false
let told = false

const GESTURES = ['pointerdown', 'keydown', 'touchstart'] as const

function autoplayRefusal(err: unknown): boolean {
  // Chrome and Safari both use NotAllowedError for the policy block. Anything
  // else — a torn-down element, an aborted load — is a different problem and
  // must not arm a gesture handler that will never help.
  return err instanceof DOMException && err.name === 'NotAllowedError'
}

function retryAll(): void {
  stopListening()
  const waiting = [...blocked]
  blocked.clear()
  vclog('consumer', 'retrying blocked audio after a user gesture', { elements: waiting.length })
  for (const el of waiting) {
    el.play().catch((err) => {
      // Still refused: put it back and wait for the next gesture rather than
      // dropping it. Do not re-toast — they have been told.
      if (autoplayRefusal(err)) {
        blocked.add(el)
        startListening()
      }
    })
  }
  if (blocked.size === 0) told = false
}

function startListening(): void {
  if (listening) return
  listening = true
  for (const type of GESTURES) {
    document.addEventListener(type, retryAll, { once: true, capture: true })
  }
}

function stopListening(): void {
  if (!listening) return
  listening = false
  for (const type of GESTURES) {
    document.removeEventListener(type, retryAll, { capture: true })
  }
}

/**
 * Start an element, and register it for a retry if the browser says no.
 *
 * Safe to call on every bind. An element already playing resolves immediately.
 */
export function playWithAutoplayRecovery(el: HTMLMediaElement, what = 'audio'): void {
  el.play().catch((err) => {
    if (!autoplayRefusal(err)) {
      vclog('consumer', 'audio play() failed', { what, error: String((err as Error)?.name ?? err) })
      return
    }
    blocked.add(el)
    startListening()
    vclog('consumer', 'audio play() BLOCKED by autoplay policy', { what, waiting: blocked.size })
    // Once per episode, not once per peer: a room of twelve blocks twelve
    // elements for a single reason, and twelve toasts describe the same click.
    if (told) return
    told = true
    toast.warning({
      title: 'Click to enable audio',
      description:
        'Your browser is blocking sound until you interact with the page. Click anywhere to hear the meeting.',
    })
  })
}

/** Forget an element that is going away, so a retry cannot resurrect it. */
export function forgetBlockedAudio(el: HTMLMediaElement): void {
  blocked.delete(el)
  if (blocked.size === 0) {
    told = false
    stopListening()
  }
}
